import { Button } from "react-migration/components/Button";
import { Icon } from "react-migration/components/Icon";
import { LineLoading } from "react-migration/domains/dashboard/documents/components/LineLoading";
import { WorkspaceDropdownList } from "./WorkspaceDropdownList";
import { WorkspaceQuery } from "@gql/sites-api/graphql";
import clsx from "clsx";
import { memo } from "react";

interface WorkspaceDropdownMobileProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  selectedWorkspace?: WorkspaceQuery["workspace"] | null;
  loading?: boolean;
  onCreateNewWorkspace: () => void;
  onSelectWorkspace: (workspaceId: string) => void;
}

export const WorkspaceDropdownMobile = ({
  isOpen,
  setIsOpen,
  selectedWorkspace,
  loading,
  onCreateNewWorkspace,
  onSelectWorkspace,
}: WorkspaceDropdownMobileProps) => {
  return (
    <div
      data-testid="workspace-dropdown-mobile"
      className={clsx("w-full bg-background-surface", isOpen && "pt-4 top-0! absolute! z-99!")}
    >
      <div data-testid="workspace-dropdown-trigger" className="h-12 w-full px-3">
        <div className="flex flex-1 items-center justify-between relative w-full h-full overflow-hidden cursor-pointer">
          <div
            className={clsx(
              "h-full grow flex items-center justify-start gap-x-4 overflow-hidden w-full",
              "*:first:max-w-full",
              isOpen ? "*:last:shrink *:last:justify-start *:last:basis-auto *:last:overflow-hidden" : "*:first:w-full"
            )}
          >
            {isOpen && (
              <Button
                variant="tertiary"
                size="lg"
                className="ring-0! shadow-none! h-9 w-9"
                onClick={() => setIsOpen(false)}
              >
                <Icon size="md" cursor="pointer" icon="icon-lt-arrow-left-s-line" />
              </Button>
            )}
            {loading && !selectedWorkspace ? (
              <>
                <LineLoading />
              </>
            ) : (
              selectedWorkspace && (
                <Button
                  variant="clear"
                  size="lg"
                  className={clsx(
                    "w-full text-left ring-0! shadow-none! text-text-base-primary text-base font-medium flex items-center justify-start",
                    isOpen ? "justify-start" : "justify-between!"
                  )}
                  onClick={() => setIsOpen(true)}
                  tailIcon={!isOpen ? "lt-arrow-right-s-line" : undefined}
                >
                  <span className="truncate">{selectedWorkspace.title}</span>
                </Button>
              )
            )}
          </div>
        </div>
      </div>
      <div
        data-testid="workspace-dropdown-content-mobile"
        className={clsx(
          "w-0 right-0 bg-background-surface h-0 overflow-hidden transition-[width]",
          isOpen && "h-[calc(100dvh-60px)] w-dvw"
        )}
      >
        {isOpen && (
          <WorkspaceDropdownContentMobile
            onCreateNewWorkspace={onCreateNewWorkspace}
            onSelectWorkspace={onSelectWorkspace}
          />
        )}
      </div>
    </div>
  );
};

interface WorkspaceDropdownContentMobileProps {
  onCreateNewWorkspace(): void;
  onSelectWorkspace(workspaceId: string): void;
}

const WorkspaceDropdownContentMobile = memo(
  ({ onCreateNewWorkspace, onSelectWorkspace }: WorkspaceDropdownContentMobileProps) => {
    return (
      <div className={"p-4 pb-9 flex flex-col gap-y-6 w-full h-[calc(100%-var(--navbar-mobile-height))] select-none"}>
        <WorkspaceDropdownList onSelectWorkspace={onSelectWorkspace} />

        <div className="flex flex-row justify-end shrink-0 min-h-9 mt-auto w-full">
          <Button variant="primary" size="sm" className="w-auto" onClick={onCreateNewWorkspace}>
            <Icon size="lg" cursor="pointer" icon="icon-lt-add-line" />
            New workspace
          </Button>
        </div>
      </div>
    );
  }
);

WorkspaceDropdownContentMobile.displayName = "WorkspaceDropdownContentMobile";
